import React, { useState, useEffect, useRef } from 'react';
import { 
  FiSearch, FiSend, FiPaperclip, FiSmile, FiPlus, 
  FiMessageSquare, FiClock, FiFileText, FiMapPin, FiUser, FiPhone
} from 'react-icons/fi';
import Swal from 'sweetalert2';

const API_URL = 'https://app.thundermaxhome.com/api.php';

const Toast = Swal.mixin({
  toast: true, position: 'top-end', showConfirmButton: false, timer: 4000, timerProgressBar: true, background: '#1e293b', color: '#fff',
});

export default function AdminChatPage() {
  // ---------------- State สำหรับระบบแชท ----------------
  const [chatList, setChatList] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messageInput, setMessageInput] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('all');
  
  // ---------------- State สำหรับเครื่องมือฝั่งขวา ----------------
  const [searchPhone, setSearchPhone] = useState('');
  const [searchResults, setSearchResults] = useState([]); // เก็บผลลัพธ์ประวัติลูกค้า
  const [isSearching, setIsSearching] = useState(false);
  
  const [quickJob, setQuickJob] = useState({ 
    customerName: '', 
    phone: '', 
    address: '',
    problem: '',
    job_type: 'general'
  });

  const messagesEndRef = useRef(null);
  const prevChatsRef = useRef([]); 

  // ==================== Effects & Functions ====================
  useEffect(() => {
    fetchChatRooms();
    const interval = setInterval(fetchChatRooms, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (activeChat) {
      fetchMessages();
      const interval = setInterval(fetchMessages, 3000);
      return () => clearInterval(interval);
    }
  }, [activeChat]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchChatRooms = async () => {
    try {
      const res = await fetch(`${API_URL}?action=get_chat_rooms`);
      const data = await res.json();
      if (data.success) {
        const newChats = data.data;
        if (prevChatsRef.current.length > 0) {
          newChats.forEach(newChat => {
            const oldChat = prevChatsRef.current.find(c => c.customer_id === newChat.customer_id);
            if (oldChat && Number(newChat.unread) > Number(oldChat.unread)) {
              Toast.fire({ icon: 'info', title: `💬 ข้อความใหม่จาก: ${newChat.name || 'ลูกค้า'}`, text: newChat.last_msg });
            }
          });
        }
        prevChatsRef.current = newChats;
        setChatList(newChats);
      }
    } catch (err) { console.error(err); }
  };

  const fetchMessages = async () => {
    if (!activeChat) return;
    try {
      const res = await fetch(`${API_URL}?action=get_chat_messages&customer_id=${activeChat.customer_id}`);
      const data = await res.json();
      if (data.success) {
        setMessages(data.data);
        fetchChatRooms(); 
      }
    } catch (err) { console.error(err); }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageInput.trim() || !activeChat) return;
    const tempText = messageInput;
    setMessageInput(''); 
    setMessages(prev => [...prev, { sender_type: 'admin', message_text: tempText, createdAt: new Date().toISOString() }]);

    try {
      await fetch(`${API_URL}?action=send_chat_message`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: activeChat.customer_id, text: tempText, platform: activeChat.platform })
      });
      fetchChatRooms(); 
    } catch (err) {}
  };

  // 🚀 ฟังก์ชันค้นหาประวัติลูกค้า (โชว์ผลลัพธ์ในหน้าเดียวกัน)
  const handleSearchHistory = async (e) => {
    e.preventDefault();
    if (!searchPhone) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const res = await fetch(`${API_URL}?action=track_job`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: searchPhone })
      });
      const data = await res.json();
      if (data.success && data.data.length > 0) {
        setSearchResults(data.data);
      } else {
        setSearchResults([]);
        Swal.fire({ toast: true, position: 'top-end', icon: 'info', title: 'ไม่พบประวัติ', showConfirmButton: false, timer: 2000 });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSearching(false);
    }
  };

  // 🚀 ฟังก์ชันสร้างออเดอร์จัดเต็ม
  const handleFullCreateJob = async (e) => {
    e.preventDefault();
    if (!quickJob.customerName || !quickJob.phone || !quickJob.problem) {
      Swal.fire('แจ้งเตือน', 'กรุณากรอกชื่อ เบอร์โทร และอาการให้ครบถ้วน', 'warning');
      return;
    }
    
    Swal.fire({ title: 'กำลังสร้างออเดอร์...', didOpen: () => Swal.showLoading() });
    try {
      const res = await fetch(`${API_URL}?action=create_job`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(quickJob)
      });
      const data = await res.json();
      if (data.success) {
        Swal.fire('สำเร็จ!', 'สร้างออเดอร์งานใหม่เรียบร้อยแล้ว', 'success');
        setQuickJob({ customerName: '', phone: '', address: '', problem: '', job_type: 'general' }); // ล้างฟอร์ม
      } else {
        Swal.fire('ผิดพลาด', data.message, 'error');
      }
    } catch (err) {
      Swal.fire('ผิดพลาด', 'ไม่สามารถสร้างงานได้', 'error');
    }
  };

  // ---------------- UI Helpers ----------------
  const getUnreadCount = (plat) => chatList.filter(c => plat === 'all' ? true : c.platform === plat).reduce((sum, c) => sum + Number(c.unread), 0);
  const filteredChats = chatList.filter(c => selectedPlatform === 'all' || c.platform === selectedPlatform);

  const PlatformBadge = ({ type }) => {
    if (type === 'line') return <span className="bg-[#00B900] text-white text-[8px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider shadow-sm">LINE</span>;
    if (type === 'facebook') return <span className="bg-[#1877F2] text-white text-[8px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider shadow-sm">FB</span>;
    if (type === 'ig') return <span className="bg-[#E1306C] text-white text-[8px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider shadow-sm">IG</span>;
    if (type === 'web') return <span className="bg-[#d4af37] text-white text-[8px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider shadow-sm">WEB</span>;
    return <span className="bg-gray-500 text-white text-[8px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider shadow-sm">Sys</span>;
  };

  return (
    <div className="animate-fade-in flex flex-col h-[calc(100vh-6rem)]">
      
      {/* ========================================== */}
      {/* 🚀 แถบแพลตฟอร์ม (ด้านบนสุด) */}
      {/* ========================================== */}
      <div className="bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-[#334155] rounded-xl p-3 mb-4 flex items-center gap-3 shadow-sm shrink-0">
        <span className="text-xs font-bold text-gray-500 ml-2">แพลตฟอร์ม:</span>
        <div className="flex gap-2">
          <button onClick={() => setSelectedPlatform('all')} className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold transition-all ${selectedPlatform === 'all' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 dark:bg-[#0f172a] dark:text-gray-400 hover:bg-gray-200'}`}>
            ทั้งหมด {getUnreadCount('all') > 0 && <span className="bg-red-500 text-white px-1.5 py-0.5 rounded-full text-[9px]">{getUnreadCount('all')}</span>}
          </button>
          <button onClick={() => setSelectedPlatform('line')} className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold transition-all ${selectedPlatform === 'line' ? 'bg-[#00B900] text-white' : 'bg-gray-100 text-gray-600 dark:bg-[#0f172a] dark:text-gray-400 hover:bg-gray-200'}`}>
            LINE {getUnreadCount('line') > 0 && <span className="bg-red-500 text-white px-1.5 py-0.5 rounded-full text-[9px]">{getUnreadCount('line')}</span>}
          </button>
          <button onClick={() => setSelectedPlatform('facebook')} className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold transition-all ${selectedPlatform === 'facebook' ? 'bg-[#1877F2] text-white' : 'bg-gray-100 text-gray-600 dark:bg-[#0f172a] dark:text-gray-400 hover:bg-gray-200'}`}>
            Facebook {getUnreadCount('facebook') > 0 && <span className="bg-red-500 text-white px-1.5 py-0.5 rounded-full text-[9px]">{getUnreadCount('facebook')}</span>}
          </button>
          <button onClick={() => setSelectedPlatform('ig')} className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold transition-all ${selectedPlatform === 'ig' ? 'bg-gradient-to-tr from-[#f09433] via-[#dc2743] to-[#bc1888] text-white' : 'bg-gray-100 text-gray-600 dark:bg-[#0f172a] dark:text-gray-400 hover:bg-gray-200'}`}>
            Instagram {getUnreadCount('ig') > 0 && <span className="bg-red-500 text-white px-1.5 py-0.5 rounded-full text-[9px]">{getUnreadCount('ig')}</span>}
          </button>
          <button onClick={() => setSelectedPlatform('web')} className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold transition-all ${selectedPlatform === 'web' ? 'bg-[#d4af37] text-white' : 'bg-gray-100 text-gray-600 dark:bg-[#0f172a] dark:text-gray-400 hover:bg-gray-200'}`}>
            Web Chat {getUnreadCount('web') > 0 && <span className="bg-red-500 text-white px-1.5 py-0.5 rounded-full text-[9px]">{getUnreadCount('web')}</span>}
          </button>
        </div>
      </div>

      <div className="flex-1 bg-white dark:bg-[#1e293b] rounded-2xl shadow-sm border border-gray-200 dark:border-[#334155] flex overflow-hidden">
        
        {/* ========================================== */}
        {/* 📱 คอลัมน์ 1 (ซ้ายสุด): รายชื่อแชท */}
        {/* ========================================== */}
        <div className="w-[280px] lg:w-[320px] shrink-0 border-r border-gray-200 dark:border-[#334155] flex flex-col bg-gray-50/50 dark:bg-[#131825]/50">
          <div className="p-4 border-b border-gray-200 dark:border-[#334155] shrink-0">
            <h2 className="text-lg font-black text-gray-800 dark:text-white flex items-center gap-2">💬 Inbox</h2>
          </div>
          <div className="flex-1 overflow-y-auto custom-scrollbar">
            {filteredChats.length > 0 ? filteredChats.map(chat => (
              <div 
                key={chat.customer_id} 
                onClick={() => {
                  setActiveChat(chat);
                  // เมื่อกดแชท ดึงชื่อมาใส่ฟอร์มฝั่งขวาให้อัตโนมัติ!
                  setQuickJob(prev => ({ ...prev, customerName: chat.name || '' }));
                }}
                className={`p-4 border-b border-gray-100 dark:border-[#1e2330] cursor-pointer transition-colors hover:bg-blue-50 dark:hover:bg-[#1e293b] flex items-center gap-3 relative
                  ${activeChat?.customer_id === chat.customer_id ? 'bg-blue-50/80 dark:bg-[#1e293b] border-l-4 border-l-blue-500' : 'border-l-4 border-l-transparent'}
                `}
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm shrink-0 shadow-sm">
                  {(chat.name || 'C').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-center mb-0.5">
                    <h3 className={`text-sm truncate pr-2 ${activeChat?.customer_id === chat.customer_id ? 'font-bold text-blue-700 dark:text-blue-400' : 'font-bold text-gray-800 dark:text-gray-200'}`}>
                      {chat.name || 'ลูกค้า'}
                    </h3>
                  </div>
                  <div className="flex justify-between items-center">
                    <p className={`text-xs truncate pr-2 ${Number(chat.unread) > 0 ? 'text-gray-800 dark:text-white font-bold' : 'text-gray-500 dark:text-gray-400'}`}>
                      {chat.last_msg || 'ส่งข้อความ...'}
                    </p>
                    {Number(chat.unread) > 0 && (
                      <span className="bg-red-500 text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full shrink-0 shadow-sm animate-pulse">
                        {chat.unread}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )) : (
              <div className="p-10 text-center text-gray-400 text-xs">ไม่พบข้อความในหมวดนี้</div>
            )}
          </div>
        </div>

        {/* ========================================== */}
        {/* 💬 คอลัมน์ 2 (กลาง): หน้าต่างสนทนา */}
        {/* ========================================== */}
        <div className="flex-1 flex flex-col bg-white dark:bg-[#0b0f19] min-w-[350px]">
          {activeChat ? (
            <>
              <div className="h-16 px-6 border-b border-gray-200 dark:border-[#334155] flex justify-between items-center bg-white dark:bg-[#1e293b] shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold shadow-sm">
                    {(activeChat.name || 'C').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="font-black text-gray-800 dark:text-white text-sm flex items-center gap-2">
                      {activeChat.name || 'ลูกค้า'} <PlatformBadge type={activeChat.platform} />
                    </h3>
                  </div>
                </div>
              </div>

              <div className="flex-1 p-6 overflow-y-auto custom-scrollbar bg-gray-50/30 dark:bg-[#0b0f19] space-y-4">
                {messages.map(msg => (
                  <div key={msg.id || Math.random()} className={`flex flex-col ${msg.sender_type === 'admin' ? 'items-end' : 'items-start'}`}>
                    <div className={`max-w-[70%] px-4 py-2.5 text-sm shadow-sm
                      ${msg.sender_type === 'admin' 
                        ? 'bg-blue-600 text-white rounded-2xl rounded-tr-sm' 
                        : 'bg-white dark:bg-[#1e293b] text-gray-800 dark:text-gray-200 border border-gray-100 dark:border-[#334155] rounded-2xl rounded-tl-sm'
                      }
                    `}>
                      {msg.message_text}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              <div className="p-4 bg-white dark:bg-[#1e293b] border-t border-gray-200 dark:border-[#334155] shrink-0">
                <form onSubmit={handleSendMessage} className="flex items-center gap-2">
                  <button type="button" className="p-2 text-gray-400 hover:text-blue-500 transition"><FiPaperclip size={20} /></button>
                  <div className="flex-1 relative">
                    <input 
                      type="text" 
                      placeholder="พิมพ์ข้อความตอบกลับ..."
                      className="w-full bg-gray-100 dark:bg-[#0f172a] border border-transparent dark:border-[#334155] text-gray-800 dark:text-gray-200 text-sm rounded-full py-3 pl-4 pr-10 focus:outline-none focus:border-blue-500 transition-colors"
                      value={messageInput}
                      onChange={(e) => setMessageInput(e.target.value)}
                    />
                    <button type="button" className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-yellow-500"><FiSmile size={18} /></button>
                  </div>
                  <button 
                    type="submit" 
                    disabled={!messageInput.trim()}
                    className={`p-3 rounded-full flex items-center justify-center shadow-md transition-colors
                      ${messageInput.trim() ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-400 dark:bg-[#334155] cursor-not-allowed'}
                    `}
                  >
                    <FiSend size={18} className="ml-1" />
                  </button>
                </form>
              </div>
            </>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-gray-400 bg-gray-50/30 dark:bg-transparent">
              <FiMessageSquare size={64} className="mb-4 opacity-20" />
              <p className="text-lg font-bold text-gray-600 dark:text-gray-300">ยังไม่ได้เลือกแชท</p>
              <p className="text-sm mt-2">คลิกเลือกแชทเพื่อตอบลูกค้า หรือกรอกฟอร์มขวามือเพื่อคีย์งานใหม่</p>
            </div>
          )}
        </div>

        {/* ========================================== */}
        {/* 🛠️ คอลัมน์ 3 (ขวาสุด): เครื่องมือจัดการ (Tools) */}
        {/* ========================================== */}
        <div className="w-[300px] lg:w-[350px] shrink-0 border-l border-gray-200 dark:border-[#334155] flex flex-col bg-gray-50/50 dark:bg-[#131825]/50 overflow-y-auto custom-scrollbar">
          
          {/* --- โซนค้นหาประวัติ --- */}
          <div className="p-5 border-b border-gray-200 dark:border-[#334155]">
            <h3 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1"><FiClock /> เช็คประวัติลูกค้าเก่า</h3>
            <form onSubmit={handleSearchHistory} className="flex gap-2">
              <input 
                type="tel" placeholder="กรอกเบอร์โทร 10 หลัก..." 
                value={searchPhone} onChange={e => setSearchPhone(e.target.value)}
                className="flex-1 bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-[#334155] text-xs px-3 py-2.5 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-gray-800 dark:text-gray-200"
              />
              <button type="submit" disabled={isSearching} className="bg-blue-100 hover:bg-blue-200 dark:bg-[#1e293b] dark:hover:bg-[#334155] text-blue-600 dark:text-blue-400 px-3 py-2.5 rounded-lg transition">
                {isSearching ? '...' : <FiSearch size={16} />}
              </button>
            </form>

            {/* โชว์ประวัติตรงนี้เลย (Inline) */}
            {searchResults.length > 0 && (
              <div className="mt-3 space-y-2 max-h-48 overflow-y-auto custom-scrollbar pr-1">
                {searchResults.map(job => (
                  <div key={job.id} className="bg-white dark:bg-[#1e293b] p-3 rounded-xl border border-gray-100 dark:border-[#334155] shadow-sm text-xs">
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-bold text-gray-800 dark:text-white">{job.problem}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded ${job.status === 'completed' ? 'bg-green-100 text-green-600' : 'bg-orange-100 text-orange-600'}`}>{job.status}</span>
                    </div>
                    <p className="text-gray-500 dark:text-gray-400 mb-1">👨‍🔧 {job.technicianName || '-'}</p>
                    <p className="text-[10px] text-gray-400">{job.createdAt}</p>
                    {/* ปุ่มดึงข้อมูลไปสร้างงานใหม่ */}
                    <button 
                      onClick={() => setQuickJob({...quickJob, customerName: job.customerName || '', phone: job.phone || '', address: job.address || ''})}
                      className="mt-2 w-full text-center text-[10px] font-bold text-blue-600 bg-blue-50 dark:bg-blue-900/30 py-1 rounded hover:bg-blue-100 transition"
                    >
                      ดึงข้อมูลไปสร้างงานใหม่
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* --- โซนสร้างออเดอร์จัดเต็ม --- */}
          <div className="p-5">
            <h3 className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider mb-4 flex items-center gap-1"><FiPlus /> คีย์แจ้งงานใหม่</h3>
            <form onSubmit={handleFullCreateJob} className="space-y-3">
              
              <div>
                <label className="block text-[10px] font-bold text-gray-500 mb-1 uppercase">ชื่อลูกค้า</label>
                <div className="relative">
                  <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={14}/>
                  <input type="text" value={quickJob.customerName} onChange={e => setQuickJob({...quickJob, customerName: e.target.value})} className="w-full bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-[#334155] text-xs px-3 py-2.5 pl-8 rounded-lg outline-none focus:border-blue-500 text-gray-800 dark:text-white" required />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-gray-500 mb-1 uppercase">เบอร์โทรศัพท์</label>
                <div className="relative">
                  <FiPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={14}/>
                  <input type="tel" value={quickJob.phone} onChange={e => setQuickJob({...quickJob, phone: e.target.value})} className="w-full bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-[#334155] text-xs px-3 py-2.5 pl-8 rounded-lg outline-none focus:border-blue-500 text-gray-800 dark:text-white" required />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-gray-500 mb-1 uppercase">ประเภทงาน</label>
                <select value={quickJob.job_type} onChange={e => setQuickJob({...quickJob, job_type: e.target.value})} className="w-full bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-[#334155] text-xs px-3 py-2.5 rounded-lg outline-none focus:border-blue-500 text-gray-800 dark:text-white">
                  <option value="general">งานซ่อมทั่วไป</option>
                  <option value="air_cleaning">ล้างแอร์</option>
                  <option value="electrical">งานระบบไฟฟ้า</option>
                  <option value="plumbing">งานประปา</option>
                  <option value="rework">งานแก้ (รับประกัน)</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-gray-500 mb-1 uppercase">อาการเสีย / สิ่งที่ต้องการ</label>
                <div className="relative">
                  <FiFileText className="absolute left-3 top-3 text-gray-400" size={14}/>
                  <textarea value={quickJob.problem} onChange={e => setQuickJob({...quickJob, problem: e.target.value})} rows="2" className="w-full bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-[#334155] text-xs px-3 py-2 pl-8 rounded-lg outline-none focus:border-blue-500 text-gray-800 dark:text-white" required></textarea>
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-gray-500 mb-1 uppercase">ที่อยู่ / พิกัดลูกค้า</label>
                <div className="relative">
                  <FiMapPin className="absolute left-3 top-3 text-gray-400" size={14}/>
                  <textarea value={quickJob.address} onChange={e => setQuickJob({...quickJob, address: e.target.value})} rows="2" className="w-full bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-[#334155] text-xs px-3 py-2 pl-8 rounded-lg outline-none focus:border-blue-500 text-gray-800 dark:text-white"></textarea>
                </div>
              </div>

              <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold py-3 rounded-xl shadow-md transition mt-4 flex items-center justify-center gap-2">
                <FiPlus size={16} /> บันทึกและสร้างคิวงาน
              </button>
            </form>
          </div>
          
        </div>
      </div>
    </div>
  );
}